import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { replace } from 'connected-react-router';

import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Skeleton from '@material-ui/lab/Skeleton';

import { ENTITY_DATA_TYPE } from '@components/config/constants';
import { isEntity } from '@constants/endpoints';
import { formatNumber } from '@utils/shared-utils';
import {
  clearEntityNavigation,
  fetchEntityNavigationPage,
  initEntityNavigation
} from '@actions/entity-navigation-actions';

import './entity-navigation.scss';

const EntityNavigation = () => {
  const dispatch = useDispatch();
  const { dataType, id, action } = useParams();
  const [pendingNav, setPendingNav] = useState(null);
  const initKey = useRef(null);

  const isEntityType = Boolean(id) && id !== 'new' && action !== 'duplicate' && isEntity(dataType);

  const { rows, count, offset, limit, loading } = useSelector(state => state.entityNavigation);

  useEffect(() => {
    if (!isEntityType) {
      dispatch(clearEntityNavigation());
      return null;
    }

    const key = `${dataType}:${id}`;
    if (initKey.current === key) {
      return null;
    }
    initKey.current = key;
    setPendingNav(null);
    dispatch(initEntityNavigation(ENTITY_DATA_TYPE, dataType));

    return () => {
      dispatch(clearEntityNavigation());
    };
  }, [dispatch, isEntityType, dataType, id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When rows update after a cross-page fetch, navigate to the pending target
  useEffect(() => {
    if (!pendingNav || !rows || loading) {
      return;
    }
    const { targetAbsolutePos } = pendingNav;
    const targetIndex = targetAbsolutePos - offset;
    if (targetIndex >= 0 && targetIndex < rows.length) {
      const targetId = rows[targetIndex].id;
      dispatch(replace(`/${dataType}/${targetId}`));
      setPendingNav(null);
    }
  }, [rows, loading, offset, pendingNav, dataType, dispatch]);

  const entityIndex = rows ? rows.findIndex(row => String(row.id) === String(id)) : -1;
  const absolutePos = entityIndex >= 0 ? offset + entityIndex : -1;

  const handlePrev = useCallback(() => {
    const targetPos = absolutePos - 1;
    if (targetPos < 0 || !rows) {
      return;
    }
    const targetIndex = targetPos - offset;
    if (targetIndex >= 0) {
      dispatch(replace(`/${dataType}/${rows[targetIndex].id}`));
    } else {
      const newOffset = Math.max(0, offset - limit);
      setPendingNav({ targetAbsolutePos: targetPos });
      dispatch(fetchEntityNavigationPage(ENTITY_DATA_TYPE, dataType, newOffset));
    }
  }, [absolutePos, rows, offset, limit, dataType, dispatch]);

  const handleNext = useCallback(() => {
    const targetPos = absolutePos + 1;
    if (targetPos >= count || !rows) {
      return;
    }
    const targetIndex = targetPos - offset;
    if (targetIndex < rows.length) {
      dispatch(replace(`/${dataType}/${rows[targetIndex].id}`));
    } else {
      const newOffset = offset + limit;
      setPendingNav({ targetAbsolutePos: targetPos });
      dispatch(fetchEntityNavigationPage(ENTITY_DATA_TYPE, dataType, newOffset));
    }
  }, [absolutePos, count, rows, offset, limit, dataType, dispatch]);

  if (!isEntityType) {
    return null;
  }

  if (loading) {
    return (
      <div styleName="nav-container">
        <Skeleton height={20} width={120} variant="rect" />
      </div>
    );
  }

  if (absolutePos < 0) {
    return null;
  }

  return (
    <div styleName="nav-container">
      <IconButton
        aria-label="Previous entity"
        disabled={absolutePos === 0}
        onClick={handlePrev}
        size="small"
        style={{ padding: '2px' }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <span styleName="counter">
        {formatNumber(absolutePos + 1)} of {formatNumber(count)}
      </span>
      <IconButton
        aria-label="Next entity"
        disabled={absolutePos >= count - 1}
        onClick={handleNext}
        size="small"
        style={{ padding: '2px' }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

export default memo(EntityNavigation);
